/**
 * Login / logout request+response contracts — E01-F01-T06. Shared by api and web
 * (architecture.md §4: contracts are zod schemas in @eer/shared, consumed by both
 * sides; web never imports `zod` directly).
 *
 * Parity source: the legacy n8n `login`/`logout` sub-flows
 * (docs/source-n8n/reference/_common.md, sql-queries.md):
 *   login  inputs  body.email, body.password
 *          success { success:true, user:{id,email,name,role,team_id,avatar}, session_token }
 *          failure { success:false, message:'Неверный email или пароль' }
 *   logout inputs  body.session_token  →  { success:true }
 *
 * `UserPublic` is exactly the `Validate Session1` projection (`authIdentitySchema`
 * in ./auth.ts) — it deliberately EXCLUDES `password` (T06 acceptance #1). The
 * canonical on-the-wire success body `{ session_token, user }` is the existing
 * `loginResponseSchema` (./auth.ts), which the shipped web client already
 * validates; this file adds the REQUEST schemas, the logout response, and the
 * legacy discriminated-union shape (`loginResultSchema`) for n8n parity, mapping
 * a failure onto the unified `unauthorized` envelope the web `handleUnauthorized`
 * flow recognises (T06 acceptance #4).
 *
 * This file is feature-owned and is re-exported from `@eer/shared` by the serial
 * Wire pass (it must NOT edit the shared barrel itself during parallel work).
 */
import { z } from 'zod';
import { APP_ROLES, authIdentitySchema, loginResponseSchema } from '../auth.js';
/**
 * `UserPublic` — the user shape returned to the client by login / present on
 * `request.user`. Identical to the `Validate Session1` projection and WITHOUT
 * `password`. `role` is narrowed to the canonical enum here (T06 acceptance #3),
 * while `authIdentitySchema` keeps `role` a plain string to match the varchar
 * column the auth-hook reads.
 */
export const userPublicSchema = authIdentitySchema.extend({
    role: z.enum(APP_ROLES),
});
/** `POST /auth/login` request body — legacy `body.email` / `body.password`. */
export const loginRequestSchema = z.object({
    email: z.string().min(1, 'Email is required'),
    password: z.string().min(1, 'Password is required'),
});
/**
 * Canonical login SUCCESS body on the wire — `{ session_token, user }`
 * (re-exported from ./auth.ts so there is one definition the web client and the
 * route both use). `user` carries the public projection (no password).
 */
export { loginResponseSchema };
/**
 * Generic uniform login failure message — IDENTICAL for an unknown email and a
 * wrong password (T04/T06 — no user-enumeration). Legacy parity verbatim.
 */
export const LOGIN_FAILURE_MESSAGE = 'Wrong email or password';
/**
 * Legacy-parity discriminated union by `success` (T06 acceptance #2). Kept for
 * any consumer that wants the old n8n `{ success, … }` shape; the v2 REST route
 * returns the bare `loginResponseSchema` on success (HTTP 200) and the unified
 * error body on failure (HTTP 401). The failure variant mirrors the old
 * `{ success:false, message }` and is bridged to the unified envelope by
 * `unauthorizedResponseSchema` below.
 */
export const loginResultSchema = z.discriminatedUnion('success', [
    z.object({
        success: z.literal(true),
        user: userPublicSchema,
        session_token: z.string().min(1),
    }),
    z.object({
        success: z.literal(false),
        message: z.string().min(1),
    }),
]);
/** `POST /auth/logout` request body — legacy `body.session_token`. */
export const logoutRequestSchema = z.object({
    session_token: z.string().min(1).optional(),
});
/** `POST /auth/logout` response — always `{ success:true }` (idempotent). */
export const logoutResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
});
/**
 * Unified "unauthorized" signal the web `handleUnauthorized` flow recognises
 * (T06 acceptance #4). Matches the legacy session-invalid node
 * (`{ success:false, message:'unauthorized' }`) AND the v2 error envelope —
 * `error/message === 'unauthorized'`.
 */
export const unauthorizedResponseSchema = z.object({
    success: z.literal(false),
    message: z.literal('unauthorized'),
});
//# sourceMappingURL=auth-login.js.map